import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { Building2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import { AuthLayout, useAuth } from "../../../auth";
import { useInvitationPreview } from "../../../center";
import { acceptCenterInvitation } from "../../application/centerSignUp";

/**
 * The page a join link opens.
 *
 * Public, because the person holding the link may have no account yet. It says
 * which centre invited them and what it offers, and nothing about who else is
 * there — the token is a way in, not a window.
 *
 * A dead invitation is described rather than hidden. Somebody clicking an
 * expired link needs to be told it expired, or they will keep clicking it.
 */
export default function CenterJoinPage() {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { isAuthenticated, loading: authLoading } = useAuth();
    const { preview, loading, notFound } = useInvitationPreview(token);

    const [joining, setJoining] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleJoin = async () => {
        if (!token) return;
        setJoining(true);
        setError(null);
        try {
            const data = await acceptCenterInvitation(token);
            // An administrator lands in the centre; anybody else lands in the
            // application they will actually be working in.
            navigate(data.is_admin ? "/center" : "/");
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            if (detail === "invitation_email_mismatch") {
                setError(t("center.join.errorWrongAccount"));
            } else if (detail === "already_in_a_center") {
                setError(t("center.join.errorAlreadyInACenter"));
            } else if (detail === "email_not_verified") {
                setError(t("center.join.errorEmailNotVerified"));
            } else if (detail === "invitation_not_usable") {
                setError(t("center.join.errorNotUsable"));
            } else {
                setError(t("center.join.errorGeneric"));
            }
        } finally {
            setJoining(false);
        }
    };

    const body = () => {
        if (loading || authLoading) {
            return <p className="text-sm text-gray-500">{t("common.loading")}</p>;
        }

        if (notFound || !preview) {
            return (
                <p role="alert" className="text-sm text-gray-600 dark:text-gray-400">
                    {t("center.join.unknown")}
                </p>
            );
        }

        if (!preview.is_usable) {
            return (
                <p role="alert" className="text-sm text-gray-600 dark:text-gray-400">
                    {t(`center.join.dead.${preview.state}`)}
                </p>
            );
        }

        return (
            <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("center.join.invitedAs", { email: preview.masked_email })}
                </p>

                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                {isAuthenticated && (
                    /* Somebody paying for their own plan: the centre covers
                       them from now on, so their own subscription stops at the
                       end of the period already paid. Said before they press,
                       not discovered on the next statement. */
                    <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 dark:bg-white/5 dark:text-gray-400">
                        {t("center.join.ownSubscriptionNote")}
                    </p>
                )}

                {isAuthenticated ? (
                    <Button onClick={handleJoin} loading={joining} fullWidth>
                        {t("center.join.accept", { center: preview.center_name })}
                    </Button>
                ) : (
                    <div className="flex flex-col gap-3">
                        <Link to={`/signup?center_invite=${token}`}>
                            <Button fullWidth>{t("center.join.createAccount")}</Button>
                        </Link>
                        <Link
                            to="/login"
                            className="text-center text-sm text-brand-600 hover:underline"
                        >
                            {t("center.join.signIn")}
                        </Link>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <PageMeta />
            <AuthLayout>
                <div className="flex w-full max-w-md flex-col justify-center">
                    <h1 className="mb-1 flex items-center gap-2 text-2xl font-semibold text-content-primary">
                        <Building2 size={22} aria-hidden="true" />
                        {preview ? preview.center_name : t("center.join.title")}
                    </h1>
                    <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                        {preview?.center_type === "training"
                            ? t("center.join.subtitleTraining")
                            : t("center.join.subtitleTherapy")}
                    </p>
                    {body()}
                </div>
            </AuthLayout>
        </>
    );
}
